import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import React from 'react';
import {
  AppBbWaardecheckValuationSuccess,
  AppVboWaardecheckValuationSuccess,
} from '~/graphql/types';
import InputGroupDivider from '~/components/page/Apps/components/InputGroupDivider';

import AppDetailsContainer from '../../../AppDetailsContainer';
import { useRecoilState } from 'recoil';
import { AppWaardecheckFieldsFragment, StateId } from '../..';
import { mergeDeepRight } from 'ramda';
import { DeepPartial } from 'utility-types';
import AppSectionHeader from '~/components/page/Apps/components/AppSectionHeader';
import WaardecheckIntl from '../../intl';
import AppEntryItemHeader from '~/components/page/Apps/components/AppEntryItemHeader';
import { WaardecheckDefaultData } from '../../utils/getDefaultData';
import Description from '../../components/Description';
import MainPreview from '../../components/MainPreview';
import IntlInputComponent from '../../components/IntlInputComponent';
import ImageInput from '../../components/ImageInput';
import { Page } from '../../components/Preview';
import TEST_ID from './index.testid';
import Explanation from '../../components/Explanation';
import { appWaardecheckState } from '../../state';

export type Props = RouteComponentProps & {
  defaultValues: WaardecheckDefaultData;
  data: AppWaardecheckFieldsFragment;
  loading: boolean;
};

const ValuationSuccess: React.FCC<Props> = ({
  data,
  defaultValues,
  loading,
}) => {
  const original = defaultValues.valuationSuccess;
  const [updated, setUpdatedValue] = useRecoilState(
    appWaardecheckState(StateId.updated),
  );

  const updateValue = (
    value: DeepPartial<
      AppBbWaardecheckValuationSuccess | AppVboWaardecheckValuationSuccess
    >,
  ) => {
    setUpdatedValue(prev => {
      if (!prev) return prev;

      return mergeDeepRight(prev, {
        valuationSuccess: value,
      }) as any as typeof prev;
    });
  };

  if (original == null || updated == null) return null;

  return (
    <AppDetailsContainer
      header="Contactgegevens"
      loading={loading}
      dataTestId={TEST_ID.CONTAINER}
    >
      <Explanation>
        Op deze pagina worden de contactgegevens ingevoerd. Personaliseer de
        achtergrondfoto en teksten.
      </Explanation>
      <MainPreview data={updated} page={Page['/success']} />
      <AppEntryItemHeader>{WaardecheckIntl.mainImage.title}</AppEntryItemHeader>
      <Description>{WaardecheckIntl.mainImage.description}</Description>
      <ImageInput
        initialUrl={data.valuationSuccess.image?.url}
        filename={data.__typename + 'valuationsuccess'}
        s3Key={updated?.valuationSuccess.image?.s3key}
        onChange={image => updateValue({ image })}
        dataTestId={TEST_ID.IMAGE}
      />
      <InputGroupDivider large />
      <AppSectionHeader>{WaardecheckIntl.section.text}</AppSectionHeader>
      <Description>{WaardecheckIntl.section.textDescription}</Description>
      <IntlInputComponent
        title={WaardecheckIntl.heading}
        defaultValue={original.heading}
        value={updated.valuationSuccess.heading}
        onChange={heading => updateValue({ heading })}
        dataTestId={TEST_ID.HEADING}
        description={WaardecheckIntl.variables.propertyAdress}
      />
      <InputGroupDivider />
      <IntlInputComponent
        title="Disclaimer"
        defaultValue={original.disclaimer}
        value={updated.valuationSuccess.disclaimer}
        onChange={disclaimer => updateValue({ disclaimer })}
        dataTestId={TEST_ID.DISCLAIMER}
      />
      <InputGroupDivider />
      <IntlInputComponent
        title="Aanvraag versturen button"
        defaultValue={original.primaryButton}
        value={updated.valuationSuccess.primaryButton}
        onChange={primaryButton => updateValue({ primaryButton })}
        dataTestId={TEST_ID.PRIMARY_BUTTON}
      />
    </AppDetailsContainer>
  );
};

export default ValuationSuccess;
